class GetCachedComputedStyle {
  static instance: GetCachedComputedStyle | null;

  /** WeakMap keyed by element → Map keyed by pseudo selector → CSSStyleDeclaration */
  cache: WeakMap<Element, Map<string | symbol, CSSStyleDeclaration>>;

  /** Symbol to represent null/undefined pseudoElement */
  readonly NULL_PSEUDO: symbol;

  constructor() {
    // Prevent direct instantiation
    if (GetCachedComputedStyle.instance) {
      throw new Error(
        "GetCachedComputedStyle is a singleton. Use getCachedComputedStyle() function instead.",
      );
    }

    // Use WeakMap for automatic garbage collection when elements are removed
    this.cache = new WeakMap();

    // Symbol to represent null/undefined pseudoElement
    this.NULL_PSEUDO = Symbol("null-pseudo");
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): GetCachedComputedStyle {
    if (!GetCachedComputedStyle.instance) {
      GetCachedComputedStyle.instance = new GetCachedComputedStyle();
    }
    return GetCachedComputedStyle.instance;
  }

  /**
   * Enhanced getComputedStyle with caching
   * @param element - The element to get computed styles for
   * @param pseudoElt - Optional pseudo-element selector
   * @param skipCache - Whether to skip cache (default: false)
   */
  getComputedStyle(
    element: Element,
    pseudoElt: string | null = null,
    skipCache: boolean = false,
  ): CSSStyleDeclaration {
    if (!element || typeof element !== "object" || !element.nodeType) {
      throw new TypeError(
        'First argument must be an Element.\nExample: getCachedComputedStyle(document.getElementById("myElement"))',
      );
    }

    if (pseudoElt && typeof pseudoElt !== "string") {
      throw new TypeError(
        "Second argument (pseudoElt) must be a string or null\nhttps://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle#pseudoelt",
      );
    }

    if (skipCache !== undefined && typeof skipCache !== "boolean") {
      throw new TypeError(
        "Third argument (skipCache) must be a boolean\nExample: getCachedComputedStyle(element, null, true)",
      );
    }

    // If skipCache is true, bypass cache entirely
    if (skipCache) {
      return window.getComputedStyle(element, pseudoElt);
    }

    // Get or create cache for this element
    let elementCache = this.cache.get(element);
    if (!elementCache) {
      elementCache = new Map();
      this.cache.set(element, elementCache);
    }

    // Use symbol for null/undefined pseudoElement to avoid key collisions
    const pseudoKey: string | symbol = pseudoElt || this.NULL_PSEUDO;

    // Check if we have cached result for this element + pseudoElement combination
    if (elementCache.has(pseudoKey)) {
      return elementCache.get(pseudoKey)!;
    }

    // Call original getComputedStyle and cache the result
    const computedStyle = window.getComputedStyle(element, pseudoElt);
    elementCache.set(pseudoKey, computedStyle);

    return computedStyle;
  }
}

// Initialize static property outside the class (ES5-compatible)
GetCachedComputedStyle.instance = null;

// Get singleton instance and bind the method
const instance = GetCachedComputedStyle.getInstance();
const getCachedComputedStyle = instance.getComputedStyle.bind(instance);

export { GetCachedComputedStyle, getCachedComputedStyle };
